import { useState, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useMqttOperations } from './useMqttOperations';
import { ACTION_CONFIGS, SCREEN_MODE_MESSAGES } from '../unitSectionConstants';

export const useUnitOperations = (unitId) => {
  const [unitsData, setUnitsData] = useState([]);
  const [unitMode, setUnitMode] = useState(null);
  const [isManualFlushOn, setIsManualFlushOn] = useState(false);
  const [isOutOfOrderOn, setIsOutOfOrderOn] = useState(false);
  
  const { sendMqttMessage, getSensorSettings: getSensorSettingsMqtt } = useMqttOperations();

  const fetchUnits = useCallback(async () => {
    try {
      const unitsRef = collection(db, 'units');
      const unitDocsSnapshot = await getDocs(unitsRef);
      const unitsArray = unitDocsSnapshot.docs.map(doc => doc.data());
      setUnitsData(unitsArray);

      const unitWithID = unitsArray.find(unit => unit.unitID === unitId);
      
      if (unitWithID) {
        setIsManualFlushOn(unitWithID.statusInfo?._water_flush_button_active ?? false);
        setIsOutOfOrderOn(unitWithID.statusInfo?._out_of_order_active ?? false);
        
        if (unitWithID.statusInfo?._water_flush_button_active) {
          setUnitMode('manual_flush_screen');
        } else if (unitWithID.statusInfo?._out_of_order_active) {
          setUnitMode('outoforder_screen');
        }
      }
    } catch (error) {
      console.error('Error fetching units data:', error);
    }
  }, [unitId]);

  const executeUnitAction = useCallback(async (actionType) => {
    const matchingUnit = unitsData.find((unit) => unit.unitID === unitId);
    
    if (!matchingUnit) {
      console.log(`Unit with ID ${unitId} not found in unitsData`);
      return;
    }

    const config = ACTION_CONFIGS[actionType];
    if (!config) {
      console.error(`Unknown action type: ${actionType}`);
      return;
    }

    try {
      const deviceId = config.useDisplayId ? matchingUnit.devices.displayID : config.deviceId;
      await sendMqttMessage(unitId, deviceId, config.direction, config.message);
      
      if (config.logMessage) {
        console.log(config.logMessage);
      }
    } catch (error) {
      console.error(`Error executing ${actionType}:`, error);
    }
  }, [unitsData, unitId, sendMqttMessage]);

  const updateUnitMode = useCallback(async (newMode) => {
    try {
      const matchingUnit = unitsData.find((unit) => unit.unitID === unitId);
      
      if (!matchingUnit) return;

      const message = SCREEN_MODE_MESSAGES[newMode];
      if (message) {
        await sendMqttMessage(unitId, matchingUnit.devices.displayID, 'screens', JSON.stringify(message));
      }

      // Handle mode-specific logic
      switch (newMode) {
        case 'outoforder_screen':
          if (unitMode === newMode) {
            setUnitMode('welcome_screen');
            setIsOutOfOrderOn(false);
          } else {
            setUnitMode(newMode);
            setIsOutOfOrderOn(true);
          }
          break;
        case 'manual_flush_screen':
          if (unitMode === newMode) {
            setUnitMode('welcome_screen');
            setIsManualFlushOn(false);
          } else {
            setUnitMode(newMode);
            setIsManualFlushOn(true);
          }
          break;
        default:
          setUnitMode(newMode);
      }
    } catch (error) {
      console.error('Error updating unit mode:', error);
    }
  }, [unitsData, unitId, unitMode, sendMqttMessage]);

  const fetchSensorSettings = useCallback(async () => {
    const matchingUnit = unitsData.find((unit) => unit.unitID === unitId);
    if (!matchingUnit) {
      console.log(`Unit with ID ${unitId} not found in unitsData`);
      return null;
    }
    return getSensorSettingsMqtt(unitId, matchingUnit.devices.displayID);
  }, [unitsData, unitId, getSensorSettingsMqtt]);

  return {
    unitsData,
    unitMode,
    isManualFlushOn,
    isOutOfOrderOn,
    fetchUnits,
    executeUnitAction,
    updateUnitMode,
    fetchSensorSettings,
  };
};